import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentCourse, putCurrentCourse } from "@/api/Api";
import { useMostRecentCourse } from "./useMostRecentCourse";
// Imported from lib/ directly (not the feature barrel) to keep this hook free
// of the flight-school feature's component imports.
import { FLIGHT_SCHOOL_COURSE_ID } from "@/features/flight-school/lib/flightSchoolCourseData";

// iOS useCurrentCourse (client-native/src/hooks/useSelectedCourse.ts) handles
// Flight School entirely locally: a storage flag + a cache write. It NEVER
// PUTs the virtual courseId -1 to the backend (which has no FS course record).
const FS_COURSE_KEY = "flight_school_active";

function readFsOverride(): boolean {
  try {
    return localStorage.getItem(FS_COURSE_KEY) === "true";
  } catch {
    return false;
  }
}

export const useCurrentCourse = () => {
  const queryClient = useQueryClient();
  const { mostRecentCourse, isLoading: isMostRecentLoading } =
    useMostRecentCourse();

  const currentCourseQuery = useQuery({
    queryKey: ["currentCourse"],
    queryFn: () => getCurrentCourse(),
  });

  const handleSelectCourse = async (courseId: number) => {
    if (courseId === FLIGHT_SCHOOL_COURSE_ID) {
      // iOS: FS selection is local-only (flight_school_active flag +
      // setQueryData) — no backend round-trip to reject the tap.
      try {
        localStorage.setItem(FS_COURSE_KEY, "true");
      } catch {
        /* storage unavailable — the cache write below still switches */
      }
      queryClient.setQueryData(["currentCourse"], { courseId });
      return;
    }
    try {
      localStorage.removeItem(FS_COURSE_KEY);
    } catch {
      /* ignore */
    }
    // Optimistically switch the current course IMMEDIATELY so the caller can
    // navigate to Home right away (and Home shows the loading skeleton while the
    // new course's data loads) — instead of waiting on the server round-trip on
    // the Courses tab. Persist + refresh happen in the background.
    queryClient.setQueryData(["currentCourse"], { courseId });
    void (async () => {
      try {
        await putCurrentCourse({ courseId });
      } catch (err) {
        console.error("Failed to persist course selection:", err);
      }
      // Mirror iOS switchToCourse: refresh everything that depends on the
      // current course so detail/progress views reflect the switch.
      void queryClient.invalidateQueries({ queryKey: ["currentCourse"] });
      void queryClient.invalidateQueries({ queryKey: ["courseInfo"] });
      void queryClient.invalidateQueries({ queryKey: ["chapterLessons"] });
      void queryClient.invalidateQueries({ queryKey: ["coursesProgress"] });
    })();
  };

  // iOS parity (useSelectedCourse.ts): storage override wins; otherwise the
  // explicit selection, then the most-recently-worked course DERIVED from synced
  // progress, then course 1. The derivation is what makes a fresh browser / new
  // device land on the right course instead of hanging on course 1's skeleton —
  // the web used to jump straight to `|| 1` (no most-recent fallback).
  const fsOverride = readFsOverride();
  // An explicit localStorage selection (getCurrentCourse returns {} when none).
  const explicitCourseId = currentCourseQuery.data?.courseId || undefined;
  // Preserve the undefined-until-ready contract: stay undefined while the
  // currentCourse read is loading, OR while there's no explicit selection and
  // the most-recent derivation is still resolving. Only THEN settle on
  // explicit → derived → 1. Returning 1 early would let consumers that don't
  // gate on `isCurrentCourseLoading` (Courses tab / CourseView) fetch course 1
  // and run one-shot level init before the derived course id arrives.
  const currentCourseID = fsOverride
    ? FLIGHT_SCHOOL_COURSE_ID
    : currentCourseQuery.data == null ||
        (explicitCourseId == null && isMostRecentLoading)
      ? undefined
      : explicitCourseId ?? mostRecentCourse?.courseId ?? 1;

  // With an explicit selection we only wait on the (fast, localStorage)
  // currentCourse read. With NO selection we additionally wait on the
  // most-recent derivation so Home shows the skeleton until the real course is
  // known — never a flash of course 1.
  const isCurrentCourseLoading = fsOverride
    ? false
    : currentCourseQuery.isLoading ||
      (explicitCourseId == null && isMostRecentLoading);

  return {
    currentCourseID,
    isCurrentCourseLoading,
    setCurrentCourseID: handleSelectCourse,
  };
};
